import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * BackgroundVideo Component
 * 
 * Renders a high-performance background video with lazy loading and multi-codec support.
 * Designed for 60fps playback on 4K sources.
 * 
 * Logic:
 * 1. IntersectionObserver monitors proximity to viewport (rootMargin: '200px').
 * 2. When close, the <video> tag is populated with <source> elements.
 * 3. Browser selects the first compatible source based on MIME type and codec.
 * 
 * Source Order Strategy:
 * 1. AV1 (mp4): Maximum efficiency (Chrome/Edge/Android/New Apple).
 * 2. HEVC (mp4): Hardware acceleration for Safari/iOS.
 * 3. VP9 (webm): Fallback for older browsers.
 */
const BackgroundVideo = ({ poster, sources, className, options }) => {
  const videoRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observerOptions = {
      root: null,
      rootMargin: '200px', // Start loading 200px before entering viewport
      threshold: 0,
      ...options,
    };

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setIsLoaded(true);
          obs.disconnect(); // Stop observing once loaded
        }
      });
    }, observerOptions);

    observer.observe(videoElement);

    return () => {
      if (videoElement) observer.unobserve(videoElement);
    };
  }, [options]);

  return (
    <div className={`relative w-full h-full overflow-hidden ${className || ''}`}>
      {/* Poster Image (always visible initially to prevent CLS) */}
      <img
        src={poster}
        alt="Background Video Poster"
        className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ${
          isLoaded ? 'opacity-0' : 'opacity-100'
        } -z-20`}
        // Ensure the image stays behind the video but covers the area until video plays
      />

      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        poster={poster}
        className="absolute top-0 left-0 w-full h-full object-cover -z-10"
        style={{ opacity: isLoaded ? 1 : 0, transition: 'opacity 0.5s ease-in' }} // Fade in video
      >
        {isLoaded &&
          sources.map((source, index) => (
            <source key={index} src={source.src} type={source.type} />
          ))}
        Your browser does not support the video tag.
      </video>
    </div>
  );
};

BackgroundVideo.propTypes = {
  poster: PropTypes.string.isRequired,
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  className: PropTypes.string,
  options: PropTypes.object,
};

export default BackgroundVideo;
